import type { TourDef, TourId } from './types'

export const TOUR_IDS = {
  TAB_PROJECT: 'tour.tab.project',
  TAB_EDITOR: 'tour.tab.editor',
  TAB_CHAPTERS: 'tour.tab.chapters',
  TAB_SETTINGS: 'tour.tab.settings',
  TAB_PLANNING: 'tour.tab.planning',
  TAB_SNAPSHOTS: 'tour.tab.snapshots',
  TAB_COMMENTS: 'tour.tab.comments',
  TAB_ANALYSIS: 'tour.tab.analysis',
  TAB_EXPORTER: 'tour.tab.exporter',
  TAB_CONCENTRATION: 'tour.tab.concentration',
} as const satisfies Record<string, TourId>

export const ALL_TOUR_IDS: TourId[] = [
  TOUR_IDS.TAB_PROJECT,
  TOUR_IDS.TAB_EDITOR,
  TOUR_IDS.TAB_CHAPTERS,
  TOUR_IDS.TAB_SETTINGS,
  TOUR_IDS.TAB_PLANNING,
  TOUR_IDS.TAB_SNAPSHOTS,
  TOUR_IDS.TAB_COMMENTS,
  TOUR_IDS.TAB_ANALYSIS,
  TOUR_IDS.TAB_EXPORTER,
  TOUR_IDS.TAB_CONCENTRATION,
]

export const TOUR_CATALOG: Record<TourId, TourDef> = {
  [TOUR_IDS.TAB_PROJECT]: {
    id: TOUR_IDS.TAB_PROJECT,
    title: 'Pestaña Proyecto',
    steps: [
      {
        id: 'project-cover',
        title: 'Portada y datos',
        text: '<p>Aquí ves la <strong>portada</strong> y el resumen de tu obra. Puedes editar título, sinopsis y más.</p>',
        attachTo: '[data-tour="project-tab-cover"]',
        attachOn: 'right',
      },
      {
        id: 'project-dashboard',
        title: 'Panel del proyecto',
        text: '<p>Desde este panel accedes a capítulos, pendientes, fichas y comentarios sin cambiar de pestaña.</p>',
        attachTo: '[data-tour="project-tab-dashboard"]',
        attachOn: 'top',
      },
    ],
  },

  [TOUR_IDS.TAB_EDITOR]: {
    id: TOUR_IDS.TAB_EDITOR,
    title: 'Pestaña Editor',
    steps: [
      {
        id: 'editor-canvas',
        title: 'Tu espacio de escritura',
        text: '<p>Escribe tu capítulo aquí. El <strong>título</strong> va arriba del texto.</p>',
        attachTo: '[data-tour="editor-canvas"]',
        attachOn: 'top',
        scroll: 'none',
      },
      {
        id: 'editor-panel',
        title: 'Panel de herramientas',
        text: '<p>Abre el <strong>panel lateral</strong> para capítulos, fichas, formato y más sin salir del editor.</p>',
        platforms: 'desktop',
        attachTo: '[data-tour="editor-tools-panel"]',
        attachOn: 'left',
      },
      {
        id: 'editor-mobile-tools',
        title: 'Más herramientas',
        text: '<p>En el teléfono, el resto de herramientas del proyecto está en la <strong>barra inferior</strong>.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="editor-mobile-tools"]',
        attachOn: 'top',
      },
      {
        id: 'editor-help',
        title: 'Ayuda',
        text: '<p>Si te pierdes, el botón <strong>Ayuda</strong> está siempre a mano.</p>',
        attachTo: '[data-tour="help-nav"]',
        attachOn: 'bottom',
      },
    ],
  },

  [TOUR_IDS.TAB_CHAPTERS]: {
    id: TOUR_IDS.TAB_CHAPTERS,
    title: 'Pestaña Capítulos',
    steps: [
      {
        id: 'chapters-list',
        title: 'Lista de capítulos',
        text: '<p>Aquí ves todos los capítulos. Toca uno para abrirlo en el editor.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="chapters-tab-list"]',
        attachOn: 'top',
      },
      {
        id: 'chapters-add',
        title: 'Crear capítulos',
        text: '<p>Usa el botón <strong>+</strong> para agregar capítulos, partes o ilustraciones.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="chapters-tab-add"]',
        attachOn: 'bottom',
      },
    ],
  },

  [TOUR_IDS.TAB_SETTINGS]: {
    id: TOUR_IDS.TAB_SETTINGS,
    title: 'Pestaña Formato',
    steps: [
      {
        id: 'settings-font',
        title: 'Tipografía',
        text: '<p>Elige la <strong>fuente</strong> y el tamaño con los que escribes en el editor.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="settings-tab-font"]',
        attachOn: 'bottom',
      },
      {
        id: 'settings-indicators',
        title: 'Indicadores',
        text: '<p>Activa o desactiva el guardado, el conteo de palabras y la sesión de escritura en pantalla.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="settings-tab-indicators"]',
        attachOn: 'top',
      },
    ],
  },

  [TOUR_IDS.TAB_PLANNING]: {
    id: TOUR_IDS.TAB_PLANNING,
    title: 'Pestaña Planificación',
    steps: [
      {
        id: 'planning-selector',
        title: 'Herramientas',
        text: '<p>Elige qué quieres planificar: pizarra, notas, fichas, cronologías, mapas y más.</p>',
        attachTo: '[data-tour="planning-tool-selector"]',
        attachOn: 'left',
      },
      {
        id: 'planning-workspace',
        title: 'Área de trabajo',
        text: '<p>Aquí editas el contenido de la herramienta que hayas elegido.</p>',
        attachTo: '[data-tour="planning-content"]',
        attachOn: 'top',
      },
    ],
  },

  [TOUR_IDS.TAB_SNAPSHOTS]: {
    id: TOUR_IDS.TAB_SNAPSHOTS,
    title: 'Pestaña Versiones',
    steps: [
      {
        id: 'snapshots-create',
        title: 'Nueva versión',
        text: '<p>Guarda un <strong>respaldo</strong> de tu manuscrito cuando quieras congelar el estado del proyecto.</p>',
        audience: 'registered',
        attachTo: '[data-tour="snapshots-tab-create"]',
        attachOn: 'bottom',
      },
      {
        id: 'snapshots-timeline',
        title: 'Historial',
        text: '<p>Revisa versiones anteriores y comparte enlaces con <strong>lectores beta</strong>.</p>',
        audience: 'registered',
        attachTo: '[data-tour="snapshots-tab-timeline"]',
        attachOn: 'top',
      },
    ],
  },

  [TOUR_IDS.TAB_COMMENTS]: {
    id: TOUR_IDS.TAB_COMMENTS,
    title: 'Pestaña Comentarios',
    steps: [
      {
        id: 'comments-panel',
        title: 'Comentarios del capítulo',
        text: '<p>Lee y responde comentarios del capítulo que tengas abierto en el editor.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="comments-tab-panel"]',
        attachOn: 'top',
      },
    ],
  },

  [TOUR_IDS.TAB_ANALYSIS]: {
    id: TOUR_IDS.TAB_ANALYSIS,
    title: 'Pestaña Análisis',
    steps: [
      {
        id: 'analysis-mobile-terms',
        title: 'Términos frecuentes',
        text: '<p>Revisa qué palabras repites más en el capítulo actual.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="analysis-tab-terms"]',
        attachOn: 'top',
      },
      {
        id: 'analysis-desktop-overview',
        title: 'Análisis editorial',
        text: '<p>Estadísticas de todo el manuscrito: capítulos analizados, cuota mensual y estado de cada capítulo.</p>',
        platforms: 'desktop',
        audience: 'registered',
        attachTo: '[data-tour="analysis-tab-overview"]',
        attachOn: 'bottom',
      },
      {
        id: 'analysis-desktop-analyze',
        title: 'Analizar',
        text: '<p>Elige capítulos para analizar o compara resultados entre versiones.</p>',
        platforms: 'desktop',
        audience: 'registered',
        attachTo: '[data-tour="analysis-tab-analyze"]',
        attachOn: 'bottom',
      },
    ],
  },

  [TOUR_IDS.TAB_EXPORTER]: {
    id: TOUR_IDS.TAB_EXPORTER,
    title: 'Pestaña Exportar',
    steps: [
      {
        id: 'exporter-modes',
        title: 'Modos de exportación',
        text: '<p>Cambia entre exportación rápida, <strong>maquetador</strong> o importar un manuscrito.</p>',
        audience: 'registered',
        attachTo: '[data-tour="exporter-tab-modes"]',
        attachOn: 'left',
      },
      {
        id: 'exporter-quick',
        title: 'Exportar a Word',
        text: '<p>Configura tamaño, márgenes y descarga tu obra en unos clics.</p>',
        audience: 'registered',
        attachTo: '[data-tour="exporter-tab-quick"]',
        attachOn: 'top',
      },
    ],
  },

  [TOUR_IDS.TAB_CONCENTRATION]: {
    id: TOUR_IDS.TAB_CONCENTRATION,
    title: 'Modo concentración',
    steps: [
      {
        id: 'concentration-session',
        title: 'Sesión enfocada',
        text: '<p>Escribe sin distracciones. Al terminar, revisa tu tiempo y palabras en esta misma vista.</p>',
        platforms: 'mobile',
        attachTo: '[data-tour="concentration-tab-body"]',
        attachOn: 'top',
      },
    ],
  },
}

export function getTourDef(tourId: TourId): TourDef {
  return TOUR_CATALOG[tourId]
}

export function getRestartableTours(): { id: TourId; title: string }[] {
  return ALL_TOUR_IDS.map((id) => ({
    id,
    title: TOUR_CATALOG[id].title,
  }))
}
